import { Trophy, CheckCircle, Zap, Users, TrendingUp, Award, IndianRupee } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PageHeader } from "@/components/shared";
import { useMyRankStatus, useClaimRankRewardMutation } from "@/hooks/queries";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/services/httpClient";
import type { UserRankStatusDto } from "@/types/rank-reward.dto";

const ProgressRow = ({
  label,
  icon: Icon,
  current,
  required,
  progress,
  color,
}: {
  label: string;
  icon: React.ElementType;
  current: number;
  required: number;
  progress: number;
  color: string;
}) => (
  <div className="space-y-1.5">
    <div className="flex items-center justify-between text-sm">
      <span className="flex items-center gap-1.5 font-medium text-muted-foreground">
        <Icon className={`h-4 w-4 ${color}`} />
        {label}
      </span>
      <span className="text-xs font-mono">
        {current.toFixed(0)}{" "}
        <span className="text-muted-foreground">/ {required.toFixed(0)} BV</span>
      </span>
    </div>
    <Progress value={Math.min(100, progress)} className="h-2" />
    <p className="text-right text-xs text-muted-foreground">{Math.min(100, progress).toFixed(0)}%</p>
  </div>
);

const RankCard = ({ item, onClaim, isClaiming }: {
  item: UserRankStatusDto;
  onClaim: (id: number) => void;
  isClaiming: boolean;
}) => {
  const allMet = item.isAchieved;

  return (
    <Card className={`relative overflow-hidden transition-all ${allMet && !item.isClaimed ? "border-yellow-400 shadow-lg shadow-yellow-100 dark:shadow-yellow-900/20" : ""}`}>
      {allMet && !item.isClaimed && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-400" />
      )}
      {item.isClaimed && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 to-emerald-500" />
      )}

      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className={`flex h-10 w-10 items-center justify-center rounded-full ${item.isClaimed ? "bg-green-100" : allMet ? "bg-yellow-100" : "bg-muted"}`}>
              <Trophy className={`h-5 w-5 ${item.isClaimed ? "text-green-600" : allMet ? "text-yellow-600" : "text-muted-foreground"}`} />
            </div>
            <div>
              <CardTitle className="text-base">{item.rank}</CardTitle>
              <CardDescription className="text-xs flex items-center gap-1">
                <IndianRupee className="h-3 w-3" />
                Reward: ₹{item.rewardAmount.toLocaleString()}
              </CardDescription>
            </div>
          </div>

          {item.isClaimed ? (
            <Badge className="bg-green-100 text-green-700 border-green-300 shrink-0 gap-1">
              <CheckCircle className="h-3 w-3" />
              Claimed
            </Badge>
          ) : allMet ? (
            <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300 shrink-0 gap-1 animate-pulse">
              <Award className="h-3 w-3" />
              Achieved!
            </Badge>
          ) : (
            <Badge variant="outline" className="shrink-0 text-xs">
              In Progress
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Progress bars */}
        <div className="space-y-3">
          <ProgressRow
            label="Self Business"
            icon={TrendingUp}
            current={item.currentSelfBusiness}
            required={item.requiredSelfBusiness}
            progress={item.selfProgress}
            color="text-blue-500"
          />
          <ProgressRow
            label="Power Leg"
            icon={Zap}
            current={item.currentPowerBusiness}
            required={item.requiredPowerBusiness}
            progress={item.powerProgress}
            color="text-amber-500"
          />
          <ProgressRow
            label="Other Legs"
            icon={Users}
            current={item.currentOtherBusiness}
            required={item.requiredOtherBusiness}
            progress={item.otherProgress}
            color="text-purple-500"
          />
        </div>

        {/* Claim button — shows ONLY when achieved AND not yet claimed */}
        {allMet && !item.isClaimed && (
          <Button
            id={`btn-claim-rank-${item.rankRewardId}`}
            className="w-full gap-2 bg-gradient-to-r from-yellow-500 to-amber-500 text-white hover:from-yellow-600 hover:to-amber-600 shadow-md"
            disabled={isClaiming}
            onClick={() => onClaim(item.rankRewardId)}
          >
            <Award className="h-4 w-4" />
            {isClaiming ? "Processing…" : `Claim ₹${item.rewardAmount.toLocaleString()} Reward`}
          </Button>
        )}

        {/* Claimed info */}
        {item.isClaimed && item.claimedAt && (
          <p className="text-center text-xs text-muted-foreground">
            ✓ Claimed on{" "}
            {new Date(item.claimedAt).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const AccountRankRewards = () => {
  const { toast } = useToast();
  const { data: rankStatus = [], isLoading } = useMyRankStatus();
  const claimMutation = useClaimRankRewardMutation();

  const handleClaim = async (rankRewardId: number) => {
    try {
      await claimMutation.mutateAsync(rankRewardId);
      toast({
        title: "🎉 Reward Claimed!",
        description: "Congratulations! Your rank reward has been successfully claimed.",
      });
    } catch (err) {
      toast({
        title: "Claim Failed",
        description: getApiErrorMessage(err),
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Rank Rewards" description="Track your rank progress and claim rewards." />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-64 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const achieved = rankStatus.filter((r) => r.isAchieved && !r.isClaimed).length;
  const claimed = rankStatus.filter((r) => r.isClaimed).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Rank Rewards"
        description="Grow your Self, Power Leg, and Other Leg business to unlock rank rewards."
      />

      {/* Summary */}
      {rankStatus.length > 0 && (
        <div className="flex flex-wrap gap-3">
          <Badge variant="outline" className="px-3 py-1 gap-1.5 text-sm">
            <Trophy className="h-3.5 w-3.5 text-muted-foreground" />
            {rankStatus.length} Ranks Total
          </Badge>
          {achieved > 0 && (
            <Badge className="px-3 py-1 gap-1.5 text-sm bg-yellow-100 text-yellow-700 border-yellow-300">
              <Award className="h-3.5 w-3.5" />
              {achieved} Ready to Claim!
            </Badge>
          )}
          {claimed > 0 && (
            <Badge className="px-3 py-1 gap-1.5 text-sm bg-green-100 text-green-700 border-green-300">
              <CheckCircle className="h-3.5 w-3.5" />
              {claimed} Claimed
            </Badge>
          )}
        </div>
      )}

      {rankStatus.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-3 opacity-40" />
            <p className="text-sm text-muted-foreground">
              No rank rewards configured yet. Check back soon!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {rankStatus.map((item) => (
            <RankCard
              key={item.rankRewardId}
              item={item}
              onClaim={handleClaim}
              isClaiming={claimMutation.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AccountRankRewards;
